import React from 'react';
import useRect, { RectResult } from '../helpers/useRect';
import wrapEvent from '../helpers/wrapEvent';

// No action triggered yet
const IDLE = 'idle';

// we're considering showing the tooltip, but we're gonna wait a sec
const FOCUSED = 'focused';

// IT'S ON
const VISIBLE = 'visible';

// Focus has left, but we want to keep it visible for a sec
const LEAVING_VISIBLE = 'leavingVisible';

// The user clicked the tool, so we want to hide the thing, we can't just use
// IDLE because we need to ignore mousemove, etc.
const DISMISSED = 'dismissed';

const stateMachine = {
  initial: IDLE,
  states: {
    [IDLE]: {
      on: {
        mouseenter: FOCUSED,
        focus: VISIBLE,
      },
    },
    [FOCUSED]: {
      on: {
        mousemove: FOCUSED,
        mouseleave: IDLE,
        mousedown: DISMISSED,
        keydown: DISMISSED,
        blur: IDLE,
        rest: VISIBLE,
      },
    },
    [VISIBLE]: {
      on: {
        focus: FOCUSED,
        mouseenter: FOCUSED,
        mouseleave: LEAVING_VISIBLE,
        blur: LEAVING_VISIBLE,
        mousedown: DISMISSED,
        timecomplete: IDLE,
      },
    },
    [LEAVING_VISIBLE]: {
      on: {
        mouseenter: VISIBLE,
        focus: VISIBLE,
        timecomplete: IDLE,
      },
    },
    [DISMISSED]: {
      on: {
        mouseleave: IDLE,
        blur: IDLE,
      },
    },
  },
};

type Transition = keyof typeof stateMachine.states;
type TransitionEvent =
  | 'mouseenter'
  | 'mousemove'
  | 'mouseleave'
  | 'mousedown'
  | 'keydown'
  | 'focus'
  | 'blur'
  | 'rest'
  | 'timecomplete';

const transition = (state: Transition, event: TransitionEvent): Transition => {
  return stateMachine.states[state].on[event] || state;
};

interface Triggers {
  ref: React.MutableRefObject<any>;
  onMouseEnter: React.ReactEventHandler;
  onMouseMove: React.ReactEventHandler;
  onMouseLeave: React.ReactEventHandler;
  onFocus: React.ReactEventHandler;
  onBlur: React.ReactEventHandler;
  onKeyDown: React.ReactEventHandler;
  onMouseDown: React.ReactEventHandler;
  DEBUG?: boolean;
}

type TooltipValues = {
  isVisible: boolean;
  triggerRect: RectResult;
};

export default function useTooltip({
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseDown,
  ref: forwardedRef,
  DEBUG,
}: Triggers): [Triggers, TooltipValues] {
  const [state, dispatch] = React.useReducer(transition, IDLE);
  const [isVisible, setIsVisible] = React.useState(
    typeof DEBUG !== 'undefined' ? DEBUG : false,
  );

  const ownRef = React.useRef();
  const ref = forwardedRef || ownRef;
  const triggerRect = useRect(ref, isVisible);

  // If the user focuses on the element wait 100 ms to show the tooltip
  React.useEffect(() => {
    if (state === FOCUSED) {
      const timer = setTimeout(() => dispatch('rest'), 100);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [state]);

  // If we loose focus from the element, wait 500 ms to hide the tooltip
  React.useEffect(() => {
    if (state === LEAVING_VISIBLE) {
      setIsVisible(true);
      const timer = setTimeout(() => dispatch('timecomplete'), 500);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [state]);

  // Toggle tooltip visibility
  React.useEffect(() => {
    if (state === VISIBLE || state === LEAVING_VISIBLE) {
      setIsVisible(true);
    } else if (!DEBUG) {
      setIsVisible(false);
    }
  }, [state]);

  const handleMouseEnter = () => {
    switch (state) {
      case IDLE:
      case VISIBLE:
      case LEAVING_VISIBLE: {
        dispatch('mouseenter');
        break;
      }
      default:
        break;
    }
  };
  const handleMouseMove = () => {
    switch (state) {
      case FOCUSED: {
        dispatch('mousemove');
        break;
      }
      default:
        break;
    }
  };
  const handleFocus = () => {
    switch (state) {
      case IDLE:
      case VISIBLE:
      case LEAVING_VISIBLE: {
        dispatch('focus');
        break;
      }
      default:
        break;
    }
  };
  const handleBlur = () => {
    switch (state) {
      case FOCUSED:
      case VISIBLE:
      case DISMISSED: {
        dispatch('blur');
        break;
      }
      default:
        break;
    }
  };

  const handleMouseLeave = () => {
    switch (state) {
      case FOCUSED:
      case VISIBLE:
      case DISMISSED: {
        dispatch('mouseleave');
        break;
      }
      default:
        break;
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      dispatch('keydown');
    }
  };
  const handleMouseDown = () => {
    switch (state) {
      case FOCUSED:
      case VISIBLE: {
        dispatch('mousedown');
        break;
      }
      default:
        break;
    }
  };

  const trigger = {
    ref,
    onMouseEnter: wrapEvent(onMouseEnter, handleMouseEnter),
    onMouseMove: wrapEvent(onMouseMove, handleMouseMove),
    onFocus: wrapEvent(onFocus, handleFocus),
    onBlur: wrapEvent(onBlur, handleBlur),
    onMouseLeave: wrapEvent(onMouseLeave, handleMouseLeave),
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
    onMouseDown: wrapEvent(onMouseDown, handleMouseDown),
  };

  const tooltip = {
    isVisible,
    triggerRect,
  };

  return [trigger, tooltip];
}
